<template>
  <v-col
    v-if="enabled"
    :cols="cols"
    :lg="lg"
    :md="md"
  >
    <v-row
      v-if="message"
      dense
    >
      <v-col
        :aria-label="label"
        class="labeled-input"
        cols="12"
      >
        <span>{{ $bct(label) }}</span>
        <RequiredIndicator v-if="mandatory" />
      </v-col>
      <template v-if="description">
        <v-col
          class="fs-16 c-light-black my-1"
          cols="12"
        >
          <span>{{ $bct(description) }}</span>
        </v-col>
      </template>
    </v-row>
    <v-row
      v-if="maximum == undefined && minimum == undefined"
      density="compact"
    >
      <v-col
        cols="12"
        lg="4"
        md="4"
      >
        <v-select
          v-model="month"
          @update:model-value="combine()"
          :append-inner-icon="appendIcon"
          :aria-label="monthAriaLabel"
          :density="dense || veryDense ? 'compact' : undefined"
          :disabled="locked"
          :item-title="(item) => item.text"
          :items="months"
          :label="$bct('Month')"
          :readonly="readonly"
          color="primary"
          data-cy="month-select"
          variant="filled"
          hide-details
          round
          tile
        />
      </v-col>
      <v-col
        cols="12"
        lg="4"
        md="4"
      >
        <v-select
          v-model="day"
          @update:model-value="combine()"
          :append-inner-icon="appendIcon"
          :aria-label="dayAriaLabel"
          :density="dense || veryDense ? 'compact' : undefined"
          :disabled="locked"
          :item-title="(item) => $bct(item.text)"
          :items="$a.assets.month_day_options"
          :label="$bct('Day')"
          :readonly="readonly"
          color="primary"
          data-cy="day-select"
          variant="filled"
          hide-details
          round
          tile
        />
      </v-col>
      <v-col
        cols="12"
        lg="4"
        md="4"
      >
        <v-select
          v-model="year"
          @update:model-value="combine()"
          :append-inner-icon="appendIcon"
          :aria-label="yearAriaLabel"
          :density="dense || veryDense ? 'compact' : undefined"
          :disabled="locked"
          :item-title="(item) => item.text"
          :items="yearOptions"
          :label="$bct('Year')"
          :readonly="readonly"
          color="primary"
          data-cy="year-select"
          variant="filled"
          hide-details
          round
          tile
        />
      </v-col>

      <template v-if="clearable">
        <v-col cols="12">
          <v-btn
            @click="clear"
            color="primary"
            size="small"
          >
            <span>{{ $bct('Clear') }}</span>
          </v-btn>
        </v-col>
      </template>
    </v-row>
    <v-row
      v-else
      density="compact"
    >
      <v-col cols="12">
        <v-text-field
          @click="maybeOpenOrCloseDialog"
          @click:clear="clear()"
          :append-inner-icon="locked ? 'lock' : ''"
          :aria-label="ariaLabel"
          :density="dense ? 'compact' : undefined"
          :disabled="locked"
          :model-value="localeDateString"
          data-cy="labeled-date-picker"
          prepend-inner-icon="event"
          variant="filled"
          clearable
          hide-details
          readonly
          tile
          v-bind="filteredAttrs"
        />

        <v-dialog
          v-model="openDateDialog"
          width="320px"
        >
          <DatePicker
            v-model="dateModel"
            :header="$bct('Enter date')"
            :max="maximum || null"
            :min="minimum || null"
            :title="$bct('Select date')"
            scrollable
          />
        </v-dialog>
      </v-col>
    </v-row>

    <v-divider
      v-if="dividedBottom"
      class="mt-4 mb-2"
    />
  </v-col>
</template>

<script setup>
import attrsOmitDataPrefix from '@/shared/utils/filteredAttrs';
import DatePicker from '@/shared/components/DatePicker.vue';
import RequiredIndicator from '@/shared/components/RequiredIndicator.vue';
import propsToRefs from '@/shared/utils/propsToRefs';
import useAssets from '@/shared/composables/useAssets';
import { useDate } from 'vuetify';
import { useI18n } from 'vue-i18n';
import useLabeledField from '@/shared/composables/useLabeledField';
import useSchematizedField from '@/shared/composables/useSchematizedField';
import { isDateFormatValid } from '@/shared/services/date-helper';
import { onUpdated } from 'vue';

const $bct = inject('translator');

const modeledDate = defineModel({ type: undefined });

const props = defineProps({
  clearable: {
    type: Boolean,
    default: false,
  },
  dividedBottom: {
    type: Boolean,
    default: false,
  },
  maximum: {
    default: null,
    validator(value) {
      if (!value) {
        return true;
      }
      return isDateFormatValid({ name: 'maximum', value });
    },
  },
  minimum: {
    default: null,
    validator(value) {
      if (!value) {
        return true;
      }
      return isDateFormatValid({ name: 'minimum', value });
    },
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  ...useLabeledField.props,
  ...useSchematizedField.props,
});

const { assets } = useAssets();

const attrs = useAttrs();
const filteredAttrs = ref(attrsOmitDataPrefix(attrs));

const emit = defineEmits(useLabeledField.emits);
defineExpose({ attrs, emit }); // Attrs and emit are used in composable, this is included for linter

const date = useDate();
const { locale } = useI18n();

const { appendIcon, handleBlur, handleUpdateModelValue } = useLabeledField(
  ...propsToRefs(props, useLabeledField.paramKeys),
);
const { enabled, label, ariaLabel, locked } = useSchematizedField(
  ...propsToRefs(props, useSchematizedField.paramKeys),
);

onUpdated(() => {
  filteredAttrs.value = attrsOmitDataPrefix(attrs);
});

const month = ref(null);
const day = ref(null);
const openDateDialog = ref(false);
const year = ref(null);
const yearOptions = generateYearOptions();

const dateModel = computed({
  get() {
    if (!modeledDate.value) return null;
    return date.parseISO(modeledDate.value);
  },
  set(val) {
    openDateDialog.value = false;
    handleUpdateModelValue(date.toISO(val));
  },
});

const localeDateString = computed(() => {
  if (!modeledDate.value) return null;
  return new Date(modeledDate.value).toLocaleDateString(locale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'utc',
  });
});

const months = computed(() => {
  return assets.month_options.map((monthOption) => ({
    text: $bct(monthOption.text),
    value: monthOption.value,
  }));
});

const monthAriaLabel = computed(() => {
  const base = $bct('Month');
  const selectedMonth = months.value.find((monthOption) => monthOption.value === month.value);
  const selectedMonthText = selectedMonth ? selectedMonth.text : '';
  if (!props.mandatory) return selectedMonthText ? `${base}: ${selectedMonthText}` : base;

  return selectedMonth
    ? `${base}: ${selectedMonth} - ${$bct('required')}`
    : `${base} - ${$bct('required')}`;
});

const dayAriaLabel = computed(() => {
  const base = $bct('Day');
  const selectedDay = day.value;
  if (!props.mandatory) return selectedDay ? `${base}: ${selectedDay}` : base;

  return selectedDay
    ? `${base}: ${selectedDay} - ${$bct('required')}`
    : `${base} - ${$bct('required')}`;
});

const yearAriaLabel = computed(() => {
  const base = $bct('Year');
  const selectedYear = year.value;
  if (!props.mandatory) return selectedYear ? `${base}: ${selectedYear}` : base;

  return selectedYear
    ? `${base}: ${selectedYear} - ${$bct('required')}`
    : `${base} - ${$bct('required')}`;
});

watch(
  () => modeledDate.value,
  (newVal) => {
    if (newVal) split();
  },
);

onMounted(() => {
  if (modeledDate.value) split();
});

function clear() {
  year.value = null;
  month.value = null;
  day.value = null;
  handleUpdateModelValue(null);
  handleBlur(null);
}

function combine() {
  if (year.value && month.value && day.value) {
    const newValue = [year.value, month.value, day.value].join('-');
    handleUpdateModelValue(newValue);
    handleBlur(newValue);
  }
}

function generateYearOptions() {
  let currentYear;
  if (props.maximum) {
    currentYear = new Date(props.maximum).getFullYear();
  } else {
    currentYear = new Date().getFullYear();
  }

  let lastYear;
  if (props.minimum) {
    lastYear = new Date(props.minimum).getFullYear();
  } else {
    lastYear = currentYear - 95;
  }

  const years = [];
  for (let index = currentYear; index >= lastYear; index -= 1) {
    years.push({ text: index.toString(), value: index.toString() });
  }
  return years;
}

function maybeOpenOrCloseDialog() {
  if (locked.value || props.maximum === false || props.minimum === false) return;

  openDateDialog.value = !openDateDialog.value;
}

function split() {
  if (!modeledDate.value) return;

  if (modeledDate.value.includes('/')) {
    const splitDob = modeledDate.value.split('/');
    month.value = splitDob[0].padStart(2, '0');
    day.value = splitDob[1].padStart(2, '0');
    year.value = splitDob[2];
  } else {
    const splitDob = modeledDate.value.split('-');
    month.value = splitDob[1].padStart(2, '0');
    day.value = splitDob[2].padStart(2, '0');
    year.value = splitDob[0];
  }
}
</script>
